/**
 * Production Schedule Service
 *
 * Handles all production scheduling operations including:
 * - Schedule CRUD
 * - Unassigned step discovery
 * - Task splitting
 * - Daily settlement
 * - Schedule summary
 */

import httpClient from './httpClient';

const scheduleService = {
  // ==================== Schedule Query ====================

  /**
   * Get schedules within a date range
   * @param {Object} params - Query parameters
   * @param {string} params.start_date - Start date (ISO format)
   * @param {string} params.end_date - End date (ISO format)
   * @param {string} params.work_center_id - Filter by work center (optional)
   * @param {string} params.status - Filter by status (optional)
   */
  async getSchedules(params = {}) {
    try {
      const response = await httpClient.get('/api/production-schedules', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching schedules:', error);
      throw error;
    }
  },

  /**
   * Get all unassigned process steps (for sidebar)
   */
  async getUnassignedSteps() {
    try {
      const response = await httpClient.get('/api/production-schedules/unassigned-steps');
      return response.data;
    } catch (error) {
      console.error('Error fetching unassigned steps:', error);
      throw error;
    }
  },

  /**
   * Get all unassigned process steps from ALL production orders (historical data migration)
   */
  async getHistoricalSteps() {
    try {
      const response = await httpClient.get('/api/production-schedules/historical-steps');
      return response.data;
    } catch (error) {
      console.error('Error fetching historical steps:', error);
      throw error;
    }
  },

  // ==================== Schedule CRUD ====================

  /**
   * Create a new schedule entry
   * @param {Object} data - Schedule data
   */
  async createSchedule(data) {
    try {
      const response = await httpClient.post('/api/production-schedules', data);
      return response.data;
    } catch (error) {
      console.error('Error creating schedule:', error);
      throw error;
    }
  },

  /**
   * Update a schedule entry
   * @param {string} scheduleId - Schedule ID
   * @param {Object} updates - Fields to update
   */
  async updateSchedule(scheduleId, updates) {
    try {
      const response = await httpClient.put(`/api/production-schedules/${scheduleId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating schedule:', error);
      throw error;
    }
  },

  /**
   * Delete a schedule entry
   * @param {string} scheduleId - Schedule ID
   */
  async deleteSchedule(scheduleId) {
    try {
      const response = await httpClient.delete(`/api/production-schedules/${scheduleId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting schedule:', error);
      throw error;
    }
  },

  // ==================== Split Schedule ====================

  /**
   * Split a schedule entry
   * @param {string} scheduleId - Schedule ID
   * @param {number} splitQuantity - Quantity to move to new schedule
   * @param {string} notes - Optional notes
   */
  async splitSchedule(scheduleId, splitQuantity, notes = '') {
    try {
      const response = await httpClient.post(`/api/production-schedules/${scheduleId}/split`, {
        split_quantity: splitQuantity,
        notes
      });
      return response.data;
    } catch (error) {
      console.error('Error splitting schedule:', error);
      throw error;
    }
  },

  /**
   * Withdraw a split schedule, merging its quantity back to its parent.
   * Supports nested splits: all descendants are deleted and merged back.
   * @param {string} scheduleId - Schedule ID of the split child to withdraw
   */
  async withdrawSplitSchedule(scheduleId) {
    try {
      const response = await httpClient.post(`/api/production-schedules/${scheduleId}/withdraw-split`);
      return response.data;
    } catch (error) {
      console.error('Error withdrawing split schedule:', error);
      throw error;
    }
  },

  // ==================== Daily Settlement ====================

  /**
   * Settle a day's schedules with completion/scrap data
   * @param {string} date - Date to settle (ISO format)
   * @param {Array} items - Per-schedule settlement items
   */
  async dailySettle(date, items = []) {
    try {
      const response = await httpClient.post('/api/production-schedules/daily-settle', { date, items });
      return response.data;
    } catch (error) {
      console.error('Error during daily settlement:', error);
      throw error;
    }
  },

  /**
   * Reverse a daily settlement, restoring schedules to their pre-settlement state
   * @param {string} date - Date of the settlement to reverse (ISO format)
   */
  async reverseDailySettle(date) {
    try {
      const response = await httpClient.post('/api/production-schedules/daily-settle/reverse', { date });
      return response.data;
    } catch (error) {
      console.error('Error reversing daily settlement:', error);
      throw error;
    }
  },

  /**
   * List daily settlement records
   * @param {Object} params - Query params
   */
  async listDailySettlements(params = {}) {
    try {
      const response = await httpClient.get('/api/production-schedules/daily-settlements', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching daily settlements:', error);
      throw error;
    }
  },

  /**
   * Get a single daily settlement record
   * @param {string} settlementId - Settlement ID
   */
  async getDailySettlement(settlementId) {
    try {
      const response = await httpClient.get(`/api/production-schedules/daily-settlements/${settlementId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching daily settlement:', error);
      throw error;
    }
  },

  /**
   * Export schedule plan or settlement data for a date range
   * @param {Object} params - { start_date, end_date, type }
   */
  async exportSchedules(params = {}) {
    try {
      const response = await httpClient.get('/api/production-schedules/export', { params });
      return response.data;
    } catch (error) {
      console.error('Error exporting schedules:', error);
      throw error;
    }
  },

  // ==================== Process Document Export ====================

  /**
   * Export process flow document (.docx) for a production order item
   * @param {string} productionOrderId - Production order ID
   * @param {number} itemIndex - Item index in order's items array
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  async exportProcessDocument(productionOrderId, itemIndex = 0) {
    try {
      const response = await httpClient.get('/api/production-schedules/process-document', {
        params: { production_order_id: productionOrderId, item_index: itemIndex },
        responseType: 'blob',
      });

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = '工序流程单.docx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) {
          filename = decodeURIComponent(match[1]);
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error('Error exporting process document:', error);
      throw error;
    }
  },

  // ==================== Reorder ====================

  /**
   * Reorder schedules within a cell
   * @param {Array<string>} orderedIds - Schedule IDs in desired order
   */
  async reorderSchedules(orderedIds) {
    try {
      const response = await httpClient.put('/api/production-schedules/reorder', { ordered_ids: orderedIds });
      return response.data;
    } catch (error) {
      console.error('Error reordering schedules:', error);
      throw error;
    }
  },

  // ==================== Summary ====================

  /**
   * Get daily schedule summary by work center
   * @param {Object} params - Query parameters
   * @param {string} params.start_date - Start date (ISO format)
   * @param {string} params.end_date - End date (ISO format)
   */
  async getCumulativeTotals(keys = []) {
    try {
      const response = await httpClient.post('/api/production-schedules/cumulative-totals', { keys });
      return response.data;
    } catch (error) {
      console.error('Error fetching cumulative totals:', error);
      throw error;
    }
  },

  /**
   * Get schedule-based progress for a production order.
   * Aggregates daily settlement data per item/step to match the schedule board.
   * @param {string} productionOrderId - Production order ID
   */
  async getOrderScheduleProgress(productionOrderId) {
    try {
      const response = await httpClient.get(`/api/production-orders/${productionOrderId}/schedule-progress`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order schedule progress:', error);
      throw error;
    }
  },

  async getSummary(params = {}) {
    try {
      const response = await httpClient.get('/api/production-schedules/summary', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching schedule summary:', error);
      throw error;
    }
  },

  // ==================== AI Review ====================

  /**
   * Send current schedules and pending orders to backend for AI review.
   * Backend calls Qwen directly and returns suggestions.
   * @param {Object} data - { schedules, unassigned_orders, work_centers, date_range }
   */
  async aiReview(data = {}) {
    try {
      const response = await httpClient.post('/api/production-schedules/ai-review', data);
      return response.data;
    } catch (error) {
      console.error('Error during AI schedule review:', error);
      throw error;
    }
  },

  // ==================== Work Order Summary ====================

  /**
   * Work-order status summary with rule-engine progress risks and AI narrative.
   * Data is collected server-side; only the kanban date range is sent.
   * @param {Object} data - { start_date, end_date, include_ai }
   */
  async getWorkOrderSummary(data = {}) {
    try {
      const response = await httpClient.post('/api/production-schedules/work-order-summary', data);
      return response.data;
    } catch (error) {
      console.error('Error fetching work-order summary:', error);
      throw error;
    }
  },

  /**
   * Export the work-order status summary as a standalone HTML report.
   * Passes the already-generated AI text through to avoid a second LLM call.
   * @param {Object} data - { start_date, end_date, ai_summary, model }
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  async exportWorkOrderSummaryHtml(data = {}) {
    try {
      const response = await httpClient.post(
        '/api/production-schedules/work-order-summary/export-html',
        data,
        { responseType: 'blob' }
      );

      // Extract filename from Content-Disposition header (prefer UTF-8 filename*)
      const contentDisposition = response.headers['content-disposition'];
      let filename = '工单状态总结.html';
      if (contentDisposition) {
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.*?)(?:;|$)/);
        if (utf8Match && utf8Match[1]) {
          try {
            filename = decodeURIComponent(utf8Match[1]);
          } catch (e) {
            console.warn('Failed to decode UTF-8 filename:', e);
          }
        } else {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match) {
            filename = match[1];
          }
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error('Error exporting work-order summary HTML:', error);
      throw error;
    }
  },

  /**
   * Export the work-order status summary as a PDF report (for sharing).
   * Passes the already-generated AI text through to avoid a second LLM call.
   * @param {Object} data - { start_date, end_date, ai_summary, model }
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  async exportWorkOrderSummaryPdf(data = {}) {
    try {
      const response = await httpClient.post(
        '/api/production-schedules/work-order-summary/export-pdf',
        data,
        { responseType: 'blob' }
      );

      // Extract filename from Content-Disposition header (prefer UTF-8 filename*)
      const contentDisposition = response.headers['content-disposition'];
      let filename = '工单状态总结.pdf';
      if (contentDisposition) {
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.*?)(?:;|$)/);
        if (utf8Match && utf8Match[1]) {
          try {
            filename = decodeURIComponent(utf8Match[1]);
          } catch (e) {
            console.warn('Failed to decode UTF-8 filename:', e);
          }
        } else {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match) {
            filename = match[1];
          }
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error('Error exporting work-order summary PDF:', error);
      throw error;
    }
  }
};

export default scheduleService;
